import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useReferral, ReferralProvider } from '../contexts/ReferralContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, X, Home, Zap } from 'lucide-react';

// Import step components
import PatientDetailsStep from '../components/referral/PatientDetailsStep';
import BMICalculatorStep from '../components/referral/BMICalculatorStep';
import BodyPartStep from '../components/referral/BodyPartStep';
import MedicalHistoryStep from '../components/referral/MedicalHistoryStep';
import TreatmentHistoryStep from '../components/referral/TreatmentHistoryStep';
import QuestionnaireStep from '../components/referral/QuestionnaireStep';
import ClinicalFindingsStep from '../components/referral/ClinicalFindingsStep';
import ConsentStep from '../components/referral/ConsentStep';
import ReviewSubmitStep from '../components/referral/ReviewSubmitStep';
import QuickReferralSelector from '../components/referral/QuickReferralSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/logo.png';

const STEPS = [
    { id: 0, title: 'Patient Details', description: 'Demographics and contact information' },
    { id: 1, title: 'BMI Calculation', description: 'Height and weight measurements' },
    { id: 2, title: 'Condition Selection', description: 'Body part and diagnosis' },
    { id: 3, title: 'Medical History', description: 'Comorbidities and medications' },
    { id: 4, title: 'Treatment History', description: 'Previous interventions' },
    { id: 5, title: 'Questionnaire', description: 'Functional assessment scores' },
    { id: 6, title: 'Clinical Findings', description: 'Examination and imaging' },
    { id: 7, title: 'Consent', description: 'Patient consent confirmation' },
    { id: 8, title: 'Review & Submit', description: 'Confirm and submit referral' }
];

function NewReferralContent() {
    const navigate = useNavigate();
    const {
        currentStep,
        nextStep: contextNextStep,
        prevStep: contextPrevStep,
        goToStep: contextGoToStep,
        getReferralData,
        resetReferral,
        body_part,
        condition,
        applyTemplate,
        patient_consent
    } = useReferral();
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [referralResult, setReferralResult] = useState(null);
    const [showQuickReferral, setShowQuickReferral] = useState(true);

    const progress = ((currentStep + 1) / STEPS.length) * 100;

    // Scroll to top when navigating steps
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const nextStep = () => {
        contextNextStep();
        scrollToTop();
    };

    const prevStep = () => {
        contextPrevStep();
        scrollToTop();
    };

    const goToStep = (step) => {
        contextGoToStep(step);
        scrollToTop();
    };

    const handleTemplateSelect = (template) => {
        applyTemplate(template);
        setShowQuickReferral(false);
        toast.success(`Template "${template.name}" applied`);
    };

    const handleSkipTemplate = () => {
        setShowQuickReferral(false);
    };

    const handleSubmit = async () => {
        // Check if submission is allowed
        if (!canSubmit()) {
            toast.error('Patient must be willing to consider surgical intervention to submit referral');
            return;
        }
        
        setIsSubmitting(true);
        try {
            const referralData = getReferralData();
            const response = await axios.post(`${API}/referrals`, referralData);
            setReferralResult(response.data);
            toast.success('Referral submitted successfully!');
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error.response?.data?.detail || 'Failed to submit referral');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNewReferral = () => {
        resetReferral();
        setReferralResult(null);
    };

    const canProceed = () => {
        // Add validation logic for each step
        switch (currentStep) {
            case 2: // Body part step
                return body_part && condition;
            case 7: // Consent step - check surgical willingness
                return patient_consent?.willing_for_surgery === true;
            default:
                return true;
        }
    };

    // Check if referral can be submitted (surgical willingness must be yes)
    const canSubmit = () => {
        return patient_consent?.willing_for_surgery === true && patient_consent?.consent_given === true;
    };

    // Show result screen after submission
    if (referralResult) {
        return <ReferralResultScreen result={referralResult} onNewReferral={handleNewReferral} />;
    }

    // Show quick referral selector first
    if (showQuickReferral) {
        return (
            <div className="min-h-screen bg-slate-50">
                <header className="bg-white border-b border-slate-200">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/dashboard')}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                            <div className="flex items-center gap-3">
                                <Zap className="w-5 h-5 text-amber-500" />
                                <span className="font-semibold text-slate-900">Quick Referral</span>
                            </div>
                        </div>
                    </div>
                </header>
                <main className="max-w-5xl mx-auto px-4 py-8">
                    <QuickReferralSelector 
                        onSelect={handleTemplateSelect}
                        onSkip={handleSkipTemplate}
                    />
                </main>
            </div>
        );
    }

    const renderStep = () => {
        switch (currentStep) {
            case 0: return <PatientDetailsStep />;
            case 1: return <BMICalculatorStep />;
            case 2: return <BodyPartStep />;
            case 3: return <MedicalHistoryStep />;
            case 4: return <TreatmentHistoryStep />;
            case 5: return <QuestionnaireStep />;
            case 6: return <ClinicalFindingsStep />;
            case 7: return <ConsentStep />;
            case 8: return <ReviewSubmitStep onSubmit={handleSubmit} isSubmitting={isSubmitting} />;
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/dashboard')}
                                data-testid="back-to-dashboard-btn"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="FendanaCor" className="w-8 h-8 rounded-lg" />
                            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                New Referral
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Progress Bar */}
            <div className="bg-white border-b border-slate-100">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">
                            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
                        </span>
                        <span className="text-sm text-slate-500">
                            {Math.round(progress)}% complete
                        </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                </div>
            </div>

            {/* Step Navigation Pills */}
            <div className="bg-white border-b border-slate-100 overflow-x-auto">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex gap-2 min-w-max">
                        {STEPS.map((step, index) => (
                            <button
                                key={step.id}
                                onClick={() => index <= currentStep && goToStep(index)}
                                disabled={index > currentStep}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    index === currentStep
                                        ? 'bg-teal-600 text-white'
                                        : index < currentStep
                                        ? 'bg-teal-100 text-teal-700 hover:bg-teal-200 cursor-pointer'
                                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                }`}
                                data-testid={`step-pill-${index}`}
                            >
                                {index < currentStep ? (
                                    <Check className="w-3 h-3 inline mr-1" />
                                ) : null}
                                {step.title}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                {currentStep < 8 && (
                    <div className="flex justify-between mt-8">
                        <Button
                            variant="outline"
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            data-testid="prev-step-btn"
                            className="btn-secondary"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Previous
                        </Button>
                        <Button
                            onClick={nextStep}
                            disabled={!canProceed()}
                            data-testid="next-step-btn"
                            className="btn-primary"
                        >
                            Next
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                )}
            </main>
        </div>
    );
}

function ReferralResultScreen({ result, onNewReferral }) {
    const navigate = useNavigate();
    const passed = result.screening_passed;
    const urgencyLevel = result.urgency_level;

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-2xl"
            >
                <Card className={`card-clinical overflow-hidden ${
                    passed ? 'border-t-4 border-t-emerald-500' : 'border-t-4 border-t-rose-500'
                }`}>
                    <CardHeader className={`text-center py-8 ${
                        passed ? 'bg-emerald-50' : 'bg-rose-50'
                    }`}>
                        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                            passed ? 'bg-emerald-100' : 'bg-rose-100'
                        }`}>
                            {passed ? (
                                <Check className="w-10 h-10 text-emerald-600" />
                            ) : (
                                <X className="w-10 h-10 text-rose-600" />
                            )}
                        </div>
                        <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {passed ? 'Referral Submitted Successfully' : 'Referral Screening Not Passed'}
                        </CardTitle>
                        <CardDescription className="text-base mt-2">
                            {passed 
                                ? `Urgency Level: ${urgencyLevel.replace('_', ' ').toUpperCase()}`
                                : 'Additional steps required before referral can proceed'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                        {/* Patient Summary */}
                        <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-900 mb-2">Patient</h4>
                            <p className="text-slate-600">
                                {result.patient.first_name} {result.patient.last_name}
                            </p>
                            <p className="text-sm text-slate-500">
                                {result.condition.replace(/_/g, ' ')} • BMI: {result.bmi} ({result.bmi_category.replace(/_/g, ' ')})
                            </p>
                        </div>

                        {/* Urgency Score */}
                        <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-900 mb-2">Urgency Assessment</h4>
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full transition-all ${
                                                result.urgency_score >= 80 ? 'bg-rose-500' :
                                                result.urgency_score >= 60 ? 'bg-amber-500' :
                                                'bg-teal-500'
                                            }`}
                                            style={{ width: `${result.urgency_score}%` }}
                                        />
                                    </div>
                                </div>
                                <span className="font-mono font-bold text-lg">
                                    {result.urgency_score}/100
                                </span>
                            </div>
                        </div>

                        {/* Guidelines Applied */}
                        <div className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-900 mb-2">Guidelines Applied</h4>
                            <div className="flex flex-wrap gap-2">
                                {result.guidelines_applied.map((guideline, i) => (
                                    <span key={i} className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                        {guideline}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Recommendations */}
                        {result.recommendations.length > 0 && (
                            <div className={`rounded-lg p-4 ${passed ? 'bg-blue-50' : 'bg-rose-50'}`}>
                                <h4 className={`font-semibold mb-2 ${passed ? 'text-blue-900' : 'text-rose-900'}`}>
                                    {passed ? 'Clinical Recommendations' : 'Required Actions Before Re-referral'}
                                </h4>
                                <ul className="space-y-2">
                                    {result.recommendations.map((rec, i) => (
                                        <li key={i} className={`text-sm flex items-start gap-2 ${
                                            passed ? 'text-blue-700' : 'text-rose-700'
                                        }`}>
                                            <span className="mt-1">•</span>
                                            {rec}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Screening Failures */}
                        {!passed && result.screening_report.failures.length > 0 && (
                            <div className="bg-rose-50 rounded-lg p-4">
                                <h4 className="font-semibold text-rose-900 mb-2">Screening Criteria Not Met</h4>
                                <ul className="space-y-3">
                                    {result.screening_report.failures.map((failure, i) => (
                                        <li key={i} className="text-sm">
                                            <p className="font-medium text-rose-800">{failure.criterion}</p>
                                            <p className="text-rose-600">{failure.detail}</p>
                                            <p className="text-rose-500 text-xs mt-1">Guideline: {failure.guideline}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-4 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/dashboard')}
                                className="flex-1 btn-secondary"
                                data-testid="back-to-dashboard-result-btn"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                            <Button
                                onClick={onNewReferral}
                                className="flex-1 btn-primary"
                                data-testid="new-referral-result-btn"
                            >
                                New Referral
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}

export default function NewReferralPage() {
    return (
        <ReferralProvider>
            <NewReferralContent />
        </ReferralProvider>
    );
}
